import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription
}                                                                                           from 'rxjs';
import {
  ApDynGridPagerConfigBase
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynformsConfigFieldset
}                                                                                           from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynGridColumnConfigBase
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApUtilService
}                                                                                           from '../../../ap-utils/service/ap-util.service';
import {
  ApDynComponentComponent
}                                                                                           from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  distinctUntilChanged,
  filter,
  map
}                                                                                           from 'rxjs/operators';
import {
  LanguageStore
}                                                                                           from '../../../stores/translation/language.store';
import {
  FieldStore
}                                                                                           from '../../../stores/farm/field.store';
import {
  SelectableSettings
}                                                                                           from '@progress/kendo-angular-grid';
import {
  FormStore
}                                                                                           from '../../../stores/layout/form.store';
import {
  WizardStates,
  WizardUiState
}                                                                                           from './wizard-ui-config/WizardStates';
import {
  TaskManagementWizardSelectionConfig
}                                                                                           from './wizard-ui-config/task-management-wizard-selection.config';
import {
  TaskManagementWizardDetailsConfig
}                                                                                           from './wizard-ui-config/task-management-wizard-details.config';
import {
  TaskManagementWizardOverviewConfig
}                                                                                           from './wizard-ui-config/task-management-wizard-overview.config';
import {
  MapStore
}                                                                                           from '../../../stores/map/map.store';
import {
  TaskManagementDataProviders
}                                                                                           from '../../task-management-data-providers/task-management-data-providers';
import {
  cloneDeep
}                                                                                           from 'lodash';
import {
  TaskManagementStore
}                                                                                           from '../../../stores/task-management/task-management.store';
import {
  saveAs
}                                                                                           from '@progress/kendo-file-saver';
import {
  process
}                                                                                           from '@progress/kendo-data-query';
import {
  ObjectFactory
}                                                                                           from 'ts-tooling';
import {
  ITaskManagementPlanBookGridData,
  TaskManagementWizardOpenConfig,
  TaskManagementWizardOpenConfigType
}                                                                                           from '../../task-management.types';
import {
  TextProcessorService
}                                                                                           from '../../../services/common/text-processor.service';
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;
import {
  FieldArchiveStore
}                                                                                           from '../../../stores/farm/field-archive.store';

@Component({
  selector: 'task-management-wizard',
  template: `
    <task-management-report #report *ngIf="createPdfExport | async"
                            class="ap-hidden-component"
                            (exportDone)="initLeave()"
                            [planSummary]="data?.PlanSummary"
                            [items]="_selectedItems[wizardSteps.Overview]"
    ></task-management-report>
    <ap-dyncomponent
      [caption]="caption"
      [headerIcon]="'ap-icon-tasks'"
      [filterTooltip]="''"
      [canSearch]="true"
      [fieldSets]="fieldSets"
      [columns]="columns$ | async"
      [items]="items$ | async"
      [loading$]="saving$"
      [selectable]="{checkboxOnly: false, mode: 'multiple'}"
      [pager]="pager">
      <div class="ap-form-actions" dynforms.action>
        <div apResponsive
             [sizeMd]="'25-100'"
             [style.text-align]="'left'">
          <button [id]="'button_prev'"
                  kendoButton
                  [primary]="true"
                  class="k-button k-primary button-important"
                  (click)="handleStateChangeAction.emit(wizardSteps.Selection)">
            <ap-responsive-text [key]="'Global_Back_With_Arrow'"></ap-responsive-text>
          </button>
        </div>
        <div apResponsive
             [sizeMd]="'50-100'"
             [style.text-align]="'center'">
          <ap-breadcrumbs-steper [columns]="breadCrumbsColumns"
                                 [selected]="1">
          </ap-breadcrumbs-steper>
        </div>
        <div apResponsive
             [sizeMd]="'25-100'"
             [style.text-align]="'right'">
          <button [id]="'button_next'"
                  kendoButton
                  [primary]="true"
                  [disabled]="!dynComponent.valid"
                  class="k-button k-primary button-important"
                  (click)="handleStateChangeAction.emit(wizardSteps.Overview)">
            <ap-responsive-text [key]="'Global_Next_With_Arrow'"></ap-responsive-text>
          </button>
        </div>
      </div>
    </ap-dyncomponent>
  `
})
export class TaskManagementWizardComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public data: TaskManagementWizardOpenConfig;
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public fieldSets: ApDynformsConfigFieldset[] = [];
  public items$: Observable<ITaskManagementPlanBookGridData[]> = of([]);
  public allItems$: Observable<ITaskManagementPlanBookGridData[]> = of([]);
  public saving$: Observable<boolean> = of(false);
  public selectable: Observable<SelectableSettings>;
  public pager: ApDynGridPagerConfigBase;
  public wizardSteps = WizardStates;
  public handleStateChangeAction: EventEmitter<any>;
  public createPdfExport = new BehaviorSubject<boolean>(false);
  public caption = '';
  public _selectedItems: Map<WizardStates, Array<ITaskManagementPlanBookGridData>> = new Map<WizardStates, Array<ITaskManagementPlanBookGridData>>([
    [WizardStates.Selection, null],
    [WizardStates.Details, null],
    [WizardStates.Overview, null]
  ]);
  public breadCrumbsColumns = [
    WizardStates.Selection,
    WizardStates.Details,
    WizardStates.Overview
  ];

  private _uiConfigs: Map<WizardStates, WizardUiState>;
  private _stateModel: { [oldState: string]: { [newState: string]: () => void } } = {};
  private _isGridInitialized: boolean;
  private _onExportNotRunning = new BehaviorSubject<boolean>(true);
  private _onSelect = new BehaviorSubject<boolean>(false);
  private _activeWizardStep: WizardStates = WizardStates.Selection;
  private _subscriptions: Subscription[] = [];

  constructor(private fieldStore: FieldStore,
              private languageStore: LanguageStore,
              private formStore: FormStore,
              private mapStore: MapStore,
              private dataProviders: TaskManagementDataProviders,
              private taskManagementStore: TaskManagementStore,
              private textProcessorService: TextProcessorService,
              private fieldArchiveStore: FieldArchiveStore,
              private changeDetection: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.caption = this.data?.Type === TaskManagementWizardOpenConfigType.Show ? 'Global__Show' : 'Task_Mgmt_Export';
    this.handleStateChangeAction = ApUtilService.createEventEmitter(this._handleStateChange.bind(this), this._subscriptions);
    this.saving$ = combineLatest([this.taskManagementStore.Listen(s => s.saving), this.createPdfExport]).pipe(
      map(([isSaving, isGeneratingPdf]) => isSaving || isGeneratingPdf)
    );
    this._stateModel[WizardStates.Leave] = {};
    this._stateModel[WizardStates.Selection] = {};
    this._stateModel[WizardStates.Details] = {};
    this._stateModel[WizardStates.Overview] = {};
    this._stateModel[WizardStates.Export] = {};
    // Definition of actions when state changes: [oldState][newState] => (action) executed
    this._stateModel[WizardStates.Selection][WizardStates.Details] = () => this._initDetails(); // user continues from selection to details
    this._stateModel[WizardStates.Details][WizardStates.Overview] = () => this._initOverview(); // user switches from details to overview-tab
    this._stateModel[WizardStates.Overview][WizardStates.Export] = () => this._initExport(); // user clicks export in overview
    this._stateModel[WizardStates.Overview][WizardStates.Details] = () => this._initDetails(); // user goes back to details from overview
    this._stateModel[WizardStates.Details][WizardStates.Selection] = () => this._initSelection(); // user navigates from details back to selection
    this._stateModel[WizardStates.Selection][WizardStates.Leave] = () => this.initLeave(); // leaving wizard
    this._stateModel[WizardStates.Export][WizardStates.Details] = () => this._initDetails(); // after failed export user goes back to details
    this._stateModel[WizardStates.Export][WizardStates.Export] = () => this._initExport(); // export retry by user
  }

  public ngAfterContentInit(): void {
    /*
    * UI Configuration setup:
    */
    this._uiConfigs = new Map<WizardStates, WizardUiState>(
      [
        [WizardStates.Selection,
          new TaskManagementWizardSelectionConfig(this.dataProviders, this.data.PlanSummary?.SourceItem, this.handleStateChangeAction, this._onSelect)],
        [WizardStates.Details,
          new TaskManagementWizardDetailsConfig(this.dataProviders, this.data.PlanSummary?.SourceItem)],
        [WizardStates.Overview,
          new TaskManagementWizardOverviewConfig(this.dataProviders, this.data.PlanSummary?.SourceItem, this.handleStateChangeAction, this._onSelect, this._onExportNotRunning)]
      ]);
    const planSummary = this.data.PlanSummary?.SourceItem;
    const module = planSummary?.Module;
    this.allItems$ = this.data?.PlanSummary ? of(planSummary?.PlanBooks?.map<ITaskManagementPlanBookGridData>(planBook => {
      const field = this.fieldStore.Fields.find(f => f.Id === planBook.FieldId)  ??
         this.fieldArchiveStore.getFieldArchiveById(planBook.FieldId);
      const result: ITaskManagementPlanBookGridData = {
        Id: planBook.Id,
        Field: field,
        Status: this.dataProviders[module].getStatus(planBook),
        ModuleText: this.dataProviders[planBook.Module].getModuleText(planBook),
        ApplicationModeText: this.dataProviders[module].getApplicationModeText(planBook),
        OperationModeText: this.dataProviders[module].getOperationModeText(planBook),
        ElementName: this.dataProviders[module].getElementName(planBook),
        ProductName: this.dataProviders[module].getProductName(planBook),
        ProductUnitPerHa: this.dataProviders[module].getProductUnit(planBook, true),
        ProductTooltip: this.dataProviders[module].getProductTooltip(planBook),
        CropName: this.dataProviders[module].getCropName(planBook),
        AreaHa: this.dataProviders[module].getAreaHaSum(planBook),
        ApplRate: this.dataProviders[module].getApplRate(planBook),
        ApplMin: this.dataProviders[module].getApplMin(planBook),
        ApplMax: this.dataProviders[module].getApplMax(planBook),
        ApplSum: this.dataProviders[module].getApplSum(planBook),
        ApplDate: this.dataProviders[module].getDate(planBook.ApplDate),
        ExportedAt: this.dataProviders[module].getDate(planBook.ExportedAt),
        ExportTargetText: this.dataProviders[module].getExportTargetText(planBook.ExportTarget),
        SelectedExportTargetObject: undefined,
        ExportDriver: planBook?.ExportDriver,
        ExportInstrument: planBook?.ExportInstrument,
        ExportMachine: planBook?.ExportMachine,
        SourceItem: planBook
      };
      return result;
    })) : of([]);
    this._subscriptions.push(this.dynComponent.SelectedItems$.subscribe(d => this._selectedRowsChanged(d)));
    this._subscriptions.push(this.dynComponent.gridSubject.subscribe(() => this._setSelectedRows()));
    this._subscriptions.push(this.dynComponent.FormValues$.pipe(
      map((v: ITaskManagementPlanBookGridData) => v),
      filter(p => !!p && !!p.SourceItem),
      distinctUntilChanged((previous, current) => previous.SourceItem.ExportFormat === current.SourceItem.ExportFormat),
    ).subscribe(_ => {
      // depending on export format a new list of export targets needs to be set
      this.dataProviders[module]?.getExportTargets(_.SourceItem.ExportFormat);
      this.dataProviders[module]?.getSelectedExportTarget(_.SelectedExportTargetObject?.Key);
    }));
    // Handle save states
    this._subscriptions.push(combineLatest([
      this.taskManagementStore.Listen(s => s.exportFail),
      this.taskManagementStore.Listen(s => s.exportSuccess)
    ]).subscribe(([exportFail, exportSuccess]) => {
      if (this._activeWizardStep !== WizardStates.Export) {
        return;
      }
      if (exportSuccess) {
        // after export was successful in backend => always generate pdf.
        // when pdf export finished the wizard closes due to 'exportdone' event of report-component.
        this.createPdfExport.next(true);
      } else if (exportFail) {
        this._activeWizardStep = WizardStates.Overview;
        this._onExportNotRunning.next(true);
        this._setSelectedRows();
      }
    }));
    this._initSelection();
    this._subscriptions.push(this.taskManagementStore.Listen(s => s.exportedFile).subscribe(data => {
      if (data && this._selectedItems[WizardStates.Overview]?.length > 0) {
        const exportItem = this._selectedItems[WizardStates.Overview][0] as ITaskManagementPlanBookGridData;
        const filename = this.textProcessorService.replaceSpecialChars(
          this.dataProviders[module]?.getExportBaseFilename(this.data.PlanSummary.SourceItem, exportItem.SourceItem)
        );
        saveAs(new Blob([new Uint8Array(data)], {type: 'application/zip'}), `${filename}.zip`);
      }
    }));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /*
  * Function takes care of leaving the wizard
  */
  public initLeave(): void {
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  private _selectedRowsChanged(selectedPlans: ITaskManagementPlanBookGridData[]): void {
    if (!this._isGridInitialized) {
      return;
    }
    if (!selectedPlans || selectedPlans.length === 0) {
      this._onSelect.next(false);
      this._selectedItems[this._activeWizardStep] = new Array<ITaskManagementPlanBookGridData>();
      this.mapStore.Layers.TaskManagementLayer.clear();
      this.mapStore.Layers.TaskManagementNutrientLayer.clear();
    } else {
      this.mapStore.Layers.TaskManagementLayer.replaceSource(selectedPlans.map(p => p.SourceItem), this.fieldStore);
      this.mapStore.Layers.TaskManagementLayer.ZoomIn(500);
      this._onSelect.next(true);
      this._selectedItems[this._activeWizardStep] = new Array<ITaskManagementPlanBookGridData>();
      selectedPlans.forEach(row => {
        this._selectedItems[this._activeWizardStep].Add(row);
      });
      const dataProvider = this.dataProviders[selectedPlans[0].SourceItem.Module];
      const mapUrl = dataProvider?.getMapFactoryUrl(selectedPlans.map(_ => _.SourceItem));
      if (mapUrl?.length > 0) {
        const currentUrl = this.mapStore.Layers.TaskManagementNutrientLayer.source.getUrls();
        // to avoid flickering: do not update map url if it is still same
        if (currentUrl == null || currentUrl.length < 1 || currentUrl[0] !== mapUrl) {
          this.mapStore.Layers.TaskManagementNutrientLayer.Legend = dataProvider?.getMapLegend(this.data.PlanSummary?.SourceItem);
          this.mapStore.Layers.TaskManagementNutrientLayer.update(mapUrl);
          setTimeout(() => dataProvider?.selectLegendItem(selectedPlans[0].SourceItem), 0);
        }
      } else {
        this.mapStore.Layers.TaskManagementNutrientLayer.clear();
      }
    }
  }

  private _handleStateChange(newStep: WizardStates): void {
    const oldStep = this._activeWizardStep;
    this._activeWizardStep = newStep;
    if (this._stateModel[oldStep][newStep]) {
      this._stateModel[oldStep][newStep]();
    }
  }

  /*
   * Function takes care of 1. wizard step 'selection'
   * initializes data and ui.
   */
  private _initSelection(): void {
    // in case user returned from details to selection we need to store current user input
    if (this.dynComponent && this.dynComponent.dynForm && this.dynComponent.dynForm.form) {
      this._selectedItems[WizardStates.Details] = [this.dynComponent.dynForm.form.value as ITaskManagementPlanBook];
    }
    this.items$ = this.allItems$;
    this._buildUi();
    // In case nothing is pre-selected => select all on startup
    if ((!this._selectedItems[WizardStates.Selection] || this._selectedItems[WizardStates.Selection].length === 0)) {
      const fieldIdToSelect = this.data?.RouterData?.queryParams['fieldId'];
      if (fieldIdToSelect) {
        this.items$.subscribe(values => {
          const itemsToSelect = values.filter(x => x?.Field?.Id === fieldIdToSelect)
            .map(item => item.Id);
          setTimeout(() => this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect));
        }).unsubscribe();
        return;
      }
      if (this.data?.Type !== TaskManagementWizardOpenConfigType.Show) {
        this.dynComponent.dynGrid.gridPaging.onSelectAllChange('checked');
      }
    }
  }

  /*
   * Function takes care of 2. wizard step ('details') tab initialization
   */
  private _initDetails(): void {
    // Set values for details if not set already
    if (!this._selectedItems[WizardStates.Details] &&
      this._selectedItems[WizardStates.Selection] && this._selectedItems[WizardStates.Selection].length > 0) {
      this._selectedItems[WizardStates.Details] = [this._selectedItems[WizardStates.Selection][0]];
    }
    this._buildUi();
  }

  /*
  * Function takes care of last wizard step('overview') initialization
 */
  private _initOverview(): void {
    const formValues = this.dynComponent.dynForm.form.value as ITaskManagementPlanBookGridData;
    // save selection for further use by user.
    this._selectedItems[WizardStates.Details] = [formValues];
    const overviewItems = cloneDeep(this._selectedItems[WizardStates.Selection]);
    overviewItems.forEach((_: ITaskManagementPlanBookGridData) => {
      _.SourceItem.ExportFormat = formValues?.SourceItem?.ExportFormat;
      _.SourceItem.ExportTarget = formValues?.SelectedExportTargetObject?.Key;
      _.SelectedExportTargetObject = formValues?.SelectedExportTargetObject;
      _.SourceItem.ExportDriver = formValues?.SourceItem?.ExportDriver;
      _.SourceItem.ExportMachine = formValues?.SourceItem?.ExportMachine;
      _.SourceItem.ExportInstrument = formValues?.SourceItem?.ExportInstrument;
      _.SourceItem.ApplDate = formValues?.ApplDate;
      _.ApplDate = formValues?.ApplDate;
    });
    this.items$ = of(overviewItems);
    // preselect all rows for export
    this._selectedItems[WizardStates.Overview] = overviewItems;
    this._setSelectedRows();
    this._buildUi();
  }

  /*
  * Function takes care of 'Export' functionality
  */
  private _initExport(): void {
    // avoid double clicks
    if (!this._onExportNotRunning.getValue()) {
      return;
    }
    this._onExportNotRunning.next(false);
    this._onSelect.next(false);
    const plansForExport = this._selectedItems[WizardStates.Overview] as ITaskManagementPlanBookGridData[];
    this._preparePlansForExport(plansForExport);
    if (!plansForExport || plansForExport.length <= 0 || !plansForExport[0].SourceItem?.ExportFormat) {
      return;
    }
    this.taskManagementStore.export(plansForExport.map(_ => _.SourceItem));
  }

  private _setSelectedRows(): void {
    if (this._selectedItems[this._activeWizardStep] && this._selectedItems[this._activeWizardStep].length > 0 &&
      this.dynComponent.dynGrid && this.dynComponent.dynGrid.gridPaging) {
      this.dynComponent.dynGrid.gridPaging.setSelectedKeys(
        this._selectedItems[this._activeWizardStep].map((d: any) => d.Id)
      );
    }
    this._isGridInitialized = true;
  }

  /*
   * Generic function to generate/build UI depending on current wizard state.
   */
  private _buildUi(): void {
    if (!this._uiConfigs.get(this._activeWizardStep)) {
      return;
    }
    this._isGridInitialized = false;
    this._onSelect.next(this._selectedItems[this._activeWizardStep]?.length > 0);
    this.fieldSets = this._uiConfigs.get(this._activeWizardStep).getFieldsetConfig(this._selectedItems[WizardStates.Details]);
    this.columns$ = this._uiConfigs.get(this._activeWizardStep).getGridColumnConfig();
    this.pager = this._uiConfigs.get(this._activeWizardStep).getPagerConfig(this.breadCrumbsColumns, this.data?.Type);
    this.changeDetection.detectChanges();
  }

  private _preparePlansForExport(plansForExport: ITaskManagementPlanBookGridData[]): void {
    // disable paging when exporting data and getting the item's real position in grid.
    // actual real position depends on user's sorting and filtering.
    const exportGridViewState = ObjectFactory.Copy(this.dynComponent?.dynGrid?.gridPaging?.state);
    exportGridViewState.take = undefined;
    exportGridViewState.skip = undefined;
    const exportGridView = process(this.dynComponent?.dynGrid?.gridPaging?.items, exportGridViewState);

    // use gridview to get the current row positions => exported like sorted in grid
    for (const p of plansForExport) {
      p.SourceItem.ExportedAt = new Date();
      p.SourceItem.Position = exportGridView?.data?.IndexOf(p) + 1;
      p.SourceItem.CountryCode = this.languageStore?.SelectedLanguage?.Key;
    }
  }
}
